import React, { useState } from "react";
import { Loader2, AlertCircle, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { completeEventRegistration } from "@/modules/eventRegistration/features/EventRegistration/api/completeEventRegistration/completeEventRegistrationApi";
import type { CompleteEventRegistrationRequest } from "@/modules/eventRegistration/features/EventRegistration/api/completeEventRegistration/types";
import {
  calculateCheckoutPrice,
  type DiscountInfo,
} from "@/modules/eventRegistration/features/EventRegistration/utils/checkoutPriceCalculator";
import {
  computeAvailabilityChanges,
  buildAvailabilityChangeMessages,
  type AvailabilityChangeData,
} from "@/modules/eventRegistration/features/EventRegistration/utils/availabilityUtils";
import errorCodeToMessage from "@/modules/eventRegistration/features/EventRegistration/utils/errorMappings";
import { useRegistration } from "@/modules/eventRegistration/features/EventRegistration/context/RegistrationContext";

interface SecureData {
  authData: {
    clientKey: string;
    apiLoginID: string;
  };
  cardData: {
    cardNumber: string;
    month: string;
    year: string;
    cardCode: string;
    fullName: string;
  };
}

declare global {
  interface Window {
    Accept?: {
      dispatchData: (
        secureData: SecureData,
        callback: (response: AcceptResponse) => void,
      ) => void;
    };
  }
}

interface AcceptResponse {
  messages: {
    resultCode: string;
    message: Array<{
      code: string;
      text: string;
    }>;
  };
  opaqueData: {
    dataDescriptor: string;
    dataValue: string;
  };
}

interface ApiError {
  response?: {
    data?: {
      errorCode?: string;
      message?: string;
    };
  };
}

function formatCardNumber(value: string): string {
  let numericValue = value.replace(/\D/g, "");
  const isAmex = numericValue.startsWith("34") || numericValue.startsWith("37");

  if (isAmex) {
    numericValue = numericValue.slice(0, 15);
    if (numericValue.length <= 4) {
      return numericValue;
    } else if (numericValue.length <= 10) {
      return `${numericValue.slice(0, 4)} ${numericValue.slice(4)}`;
    } else {
      return `${numericValue.slice(0, 4)} ${numericValue.slice(4, 10)} ${numericValue.slice(10)}`;
    }
  } else {
    numericValue = numericValue.slice(0, 16);
    const chunks = numericValue.match(/.{1,4}/g);
    return chunks ? chunks.join(" ") : numericValue;
  }
}

function generateUniqueInvoiceNumber(): string {
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, "0");
  return `INV${timestamp}${random}`;
}

interface PaymentFormStepProps {
  total: number;
  isProcessing: boolean;
  setIsProcessing: (value: boolean) => void;
  handlePaymentSuccess: (eventCheckoutUuid: string) => void;
  disabled?: boolean;
  buildRequestPayload: (
    dataDescriptor: string,
    dataValue: string,
    amount: number,
    invoiceNumber: string,
    cardExpiration?: string,
  ) => CompleteEventRegistrationRequest;
}

function PaymentFormStep({
  total = 0,
  isProcessing = false,
  setIsProcessing,
  handlePaymentSuccess,
  disabled = false,
  buildRequestPayload,
}: PaymentFormStepProps) {
  const {
    eventInfo,
    attendees,
    voucherQuantity,
    discountCode,
    discountValueType,
    discountRawValue,
    adminDiscountType,
    adminDiscountValue,
  } = useRegistration();

  const [cardholderName, setCardholderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryMonth, setExpiryMonth] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [cardCode, setCardCode] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState(
    generateUniqueInvoiceNumber(),
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [showChangesDialog, setShowChangesDialog] = useState(false);
  const [availabilityChanges, setAvailabilityChanges] = useState<string[]>([]);
  const [availabilityMessage, setAvailabilityMessage] = useState<string | null>(
    null,
  );
  const [changeData, setChangeData] = useState<AvailabilityChangeData | null>(
    null,
  );
  const [pendingSecureData, setPendingSecureData] = useState<SecureData | null>(
    null,
  );
  const [updatedTotal, setUpdatedTotal] = useState<number | null>(null);

  const apiLoginID = process.env.REACT_APP_AUTHNET_API_LOGIN_ID || "";
  const clientKey = process.env.REACT_APP_AUTHNET_CLIENT_KEY || "";
  const eventPrice = eventInfo?.eventPrice ?? 0;
  const availableSeats = eventInfo?.availableSeats ?? 0;

  const regenerateInvoiceNumber = () => {
    setInvoiceNumber(generateUniqueInvoiceNumber());
  };

  const handleAcceptResponse = async (
    response: AcceptResponse,
    skipAvailabilityCheck = false,
  ) => {
    if (response.messages.resultCode === "Error") {
      if (
        Array.isArray(response.messages.message) &&
        response.messages.message.length
      ) {
        const userMessages = response.messages.message.map((msg) => {
          return errorCodeToMessage[msg.code] || msg.text;
        });
        setErrorMessage(userMessages.join("\n"));
      }
      setIsProcessing(false);
      return;
    }
    setErrorMessage("");
    const { dataDescriptor, dataValue } = response.opaqueData;
    try {
      const amountToCharge = updatedTotal !== null ? updatedTotal : total;
      const normalizedMonth = expiryMonth.padStart(2, "0");
      const normalizedYear =
        expiryYear.length === 2 ? `20${expiryYear}` : expiryYear;
      const cardExpiration = `${normalizedYear}-${normalizedMonth}`;
      const payload = buildRequestPayload(
        dataDescriptor,
        dataValue,
        amountToCharge,
        invoiceNumber,
        cardExpiration,
      );

      // Pass expectedAvailableSeats on first attempt so backend can verify
      if (!skipAvailabilityCheck) {
        payload.expectedAvailableSeats = availableSeats;
      }

      const result = await completeEventRegistration(payload);

      // Handle availability mismatch — backend returns before creating any records
      if (
        result.data.requiresConfirmation &&
        result.data.actualAvailableSeats !== undefined
      ) {
        const newAvailableSeats = result.data.actualAvailableSeats;
        const selectedCount = attendees.filter((a) => a.isSelected).length;
        const changes = computeAvailabilityChanges(
          selectedCount,
          availableSeats,
          newAvailableSeats,
        );
        const messages = buildAvailabilityChangeMessages(
          availableSeats,
          newAvailableSeats,
        );

        setChangeData(changes);
        setAvailabilityChanges(messages.changes);
        setAvailabilityMessage(messages.message);

        // Calculate updated pricing with new seating
        const codeDiscountInfo: DiscountInfo | null =
          discountCode && discountValueType
            ? { type: discountValueType, value: discountRawValue }
            : null;
        const adminDiscountInfo: DiscountInfo | null = adminDiscountType
          ? { type: adminDiscountType, value: adminDiscountValue }
          : null;
        const pricingAfterChanges = calculateCheckoutPrice(
          attendees
            .filter((a) => a.isSelected)
            .map((_, i) => ({
              isSelected: true,
              isWaitlist: i >= newAvailableSeats,
            })),
          eventPrice,
          voucherQuantity,
          codeDiscountInfo,
          adminDiscountInfo,
        );
        setUpdatedTotal(pricingAfterChanges.total);

        // Store card data for re-tokenization if user accepts changes
        setPendingSecureData(buildSecureData());
        setShowChangesDialog(true);
        setIsProcessing(false);
        return;
      }

      setIsProcessing(false);

      if (result.data.success && result.data.eventCheckoutUuid) {
        handlePaymentSuccess(result.data.eventCheckoutUuid);
      }
    } catch (error: unknown) {
      setIsProcessing(false);
      const apiError = error as ApiError;
      const errorMsg = apiError.response?.data?.message;
      setErrorMessage(
        errorMsg ||
          "An error occurred while processing your payment. Please try again.",
      );
      regenerateInvoiceNumber();
    }
  };

  const proceedWithPayment = () => {
    setShowChangesDialog(false);
    setIsProcessing(true);
    // Re-tokenize stored card data and submit WITHOUT expectedAvailableSeats
    if (
      pendingSecureData &&
      window.Accept &&
      typeof window.Accept.dispatchData === "function"
    ) {
      window.Accept.dispatchData(
        pendingSecureData,
        (response: AcceptResponse) => handleAcceptResponse(response, true),
      );
    } else {
      setIsProcessing(false);
      setErrorMessage(
        "Payment functionality is currently unavailable. Please contact support.",
      );
    }
  };

  const handleCancelAfterChanges = () => {
    setShowChangesDialog(false);
    setIsProcessing(false);
    setChangeData(null);
    setUpdatedTotal(null);
  };

  const buildSecureData = (): SecureData => ({
    authData: { clientKey, apiLoginID },
    cardData: {
      cardNumber: cardNumber.replace(/\s+/g, ""),
      month: expiryMonth,
      year: expiryYear,
      cardCode,
      fullName: cardholderName,
    },
  });

  const handlePayment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsProcessing(true);
    setErrorMessage("");
    regenerateInvoiceNumber();

    // Tokenize card immediately — availability is checked server-side inside the lock
    const secureData = buildSecureData();

    if (window.Accept && typeof window.Accept.dispatchData === "function") {
      window.Accept.dispatchData(secureData, handleAcceptResponse);
    } else {
      setIsProcessing(false);
      setErrorMessage(
        "Payment functionality is currently unavailable. Please contact support.",
      );
    }
  };

  return (
    <>
      <div>
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
          Payment Information
        </h3>
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
            {errorMessage}
          </div>
        )}
        <form className="space-y-4" id="payment-form" onSubmit={handlePayment}>
          <div className="space-y-2">
            <Label htmlFor="cardholderName">Cardholder Name</Label>
            <Input
              disabled={isProcessing}
              id="cardholderName"
              onChange={(e) => setCardholderName(e.target.value)}
              placeholder="John Doe"
              required
              type="text"
              value={cardholderName}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cardNumber">Card Number</Label>
            <Input
              disabled={isProcessing}
              id="cardNumber"
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              placeholder="4111 1111 1111 1111"
              required
              type="text"
              value={cardNumber}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expiryMonth">Month</Label>
              <Input
                disabled={isProcessing}
                id="expiryMonth"
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "");
                  value = value.slice(0, 2);
                  setExpiryMonth(value);
                }}
                placeholder="MM"
                required
                type="text"
                value={expiryMonth}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiryYear">Year</Label>
              <Input
                disabled={isProcessing}
                id="expiryYear"
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "");
                  value = value.slice(0, 2);
                  setExpiryYear(value);
                }}
                placeholder="YY"
                required
                type="text"
                value={expiryYear}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cardCode">CVV</Label>
              <Input
                disabled={isProcessing}
                id="cardCode"
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "");
                  value = value.slice(0, 4);
                  setCardCode(value);
                }}
                placeholder="123"
                required
                type="text"
                value={cardCode}
              />
            </div>
          </div>
        </form>
      </div>

      <Dialog onOpenChange={setShowChangesDialog} open={showChangesDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Seat Availability Has Changed
            </DialogTitle>
            <DialogDescription className="pt-4">
              {availabilityMessage && (
                <Alert className="mb-4">
                  <AlertDescription>{availabilityMessage}</AlertDescription>
                </Alert>
              )}
              {changeData && (
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-3">
                    <h4 className="font-medium flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      Attendee Changes
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Previously</p>
                        <p className="font-medium">
                          {changeData.previousSeatedCount} paid seats
                        </p>
                        {changeData.previousWaitlistCount > 0 && (
                          <p className="text-amber-600">
                            {changeData.previousWaitlistCount} waitlisted
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-muted-foreground">Now</p>
                        <p className="font-medium">
                          {changeData.newSeatedCount} paid seats
                        </p>
                        {changeData.newWaitlistCount > 0 && (
                          <p className="text-amber-600">
                            {changeData.newWaitlistCount} waitlisted
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {updatedTotal !== null && updatedTotal !== total && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-2">
                      <h4 className="font-medium">Price Update</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Previous total:
                          </span>
                          <span className="line-through">
                            ${total.toFixed(2)}
                          </span>
                        </div>
                        <Separator className="my-1" />
                        <div className="flex justify-between font-medium">
                          <span>New total:</span>
                          <span className="text-lg">
                            ${updatedTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {availabilityChanges.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-medium text-sm">What happened:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                        {availabilityChanges.map((change, index) => (
                          <li key={index}>{change}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button onClick={handleCancelAfterChanges} variant="outline">
              Cancel Payment
            </Button>
            <Button onClick={proceedWithPayment}>
              Continue with $
              {updatedTotal !== null
                ? updatedTotal.toFixed(2)
                : total.toFixed(2)}{" "}
              Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PaymentFormStep;
